import { useMemo, useState } from 'react'
import { Box, Breadcrumbs, Typography, Stack, Button, Chip, IconButton } from '@mui/material'
import { Add, Edit, FactCheck, Inventory2, RequestQuote } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useMutation, useQuery } from '@tanstack/react-query'
import { listClientRequests, getClientRequest, getAtpEstimate, type ClientRequestListItem, type RequestQualification } from '../../api/cereri'
import ClientRequestFormDialog from './ClientRequestFormDialog'
import QualifyRequestDialog from './QualifyRequestDialog'
import AtpEstimateDialog from './AtpEstimateDialog'
import OfferFormDialog, { type OfferPrefill } from './OfferFormDialog'

const QUALIFICATION_LABEL: Record<RequestQualification, string> = {
  InAnaliza: 'În analiză', Acceptata: 'Acceptată', Respinsa: 'Respinsă',
}
const QUALIFICATION_COLOR: Record<RequestQualification, 'default' | 'success' | 'error'> = {
  InAnaliza: 'default', Acceptata: 'success', Respinsa: 'error',
}

function formatResponseTime(iso: string | null): string {
  if (!iso) return '—'
  const m = /^(\d+)\.(\d+):(\d+):(\d+)|(\d+):(\d+):(\d+)/.exec(iso)
  if (!m) return iso
  const days = m[1] ? Number(m[1]) : 0
  const hours = Number(m[2] ?? m[5])
  return days > 0 ? `${days}z ${hours}h` : `${hours}h`
}

export default function CereriPage() {
  const [formTarget, setFormTarget] = useState<ClientRequestListItem | null | 'new'>(null)
  const [qualifyTarget, setQualifyTarget] = useState<ClientRequestListItem | null>(null)
  const [atpTarget, setAtpTarget] = useState<ClientRequestListItem | null>(null)
  const [offerRequestId, setOfferRequestId] = useState<string | null>(null)
  const [offerPrefill, setOfferPrefill] = useState<OfferPrefill | null>(null)
  const { data, isLoading } = useQuery({ queryKey: ['comercial', 'cereri'], queryFn: listClientRequests })

  // FR-CER-007 — propagă clientul, liniile și termenul ATP (deja calculat, fără
  // recalculare) în oferta nouă, fără reintroducere manuală.
  const createOfferMutation = useMutation({
    mutationFn: async (requestId: string) => {
      const [detail, atp] = await Promise.all([getClientRequest(requestId), getAtpEstimate(requestId)])
      return { requestId, detail, atp }
    },
    onSuccess: ({ requestId, detail, atp }) => {
      setOfferPrefill({
        clientId: detail.clientId, contactId: detail.contactId, estimatedDeliveryTerm: atp.summary,
        lines: detail.lines.map((l) => ({ articleId: l.articleId, customDescription: l.freeTextDescription ?? '', quantityThousands: String(l.quantityThousands) })),
      })
      setOfferRequestId(requestId)
    },
  })

  const columns = useMemo<GridColDef<ClientRequestListItem>[]>(() => [
    { field: 'number', headerName: 'Nr.', width: 130, renderCell: (p) => <code>{p.row.number}</code> },
    { field: 'requestedDate', headerName: 'Data', width: 110 },
    { field: 'clientName', headerName: 'Client', flex: 1, minWidth: 180 },
    { field: 'source', headerName: 'Canal', width: 100 },
    { field: 'requestedByUserName', headerName: 'Înregistrată de', width: 150 },
    {
      field: 'qualification', headerName: 'Calificare', width: 150, sortable: false,
      renderCell: (p) => <Chip size="small" label={QUALIFICATION_LABEL[p.row.qualification]} color={QUALIFICATION_COLOR[p.row.qualification]} variant="outlined" />,
    },
    { field: 'rejectionReason', headerName: 'Motiv respingere', width: 170, valueGetter: (v: string | null) => v ?? '' },
    { field: 'responseTime', headerName: 'Timp de răspuns', width: 130, renderCell: (p) => formatResponseTime(p.row.responseTime) },
    {
      field: 'actions', headerName: '', width: 130, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <Stack direction="row">
          {p.row.qualification === 'InAnaliza' && (
            <IconButton size="small" onClick={() => setFormTarget(p.row)}><Edit fontSize="small" /></IconButton>
          )}
          <IconButton size="small" title="Estimare livrare (ATP)" onClick={() => setAtpTarget(p.row)}><Inventory2 fontSize="small" /></IconButton>
          {p.row.qualification === 'Acceptata' && (
            <IconButton size="small" title="Creează ofertă" onClick={() => createOfferMutation.mutate(p.row.id)}>
              <RequestQuote fontSize="small" />
            </IconButton>
          )}
          <IconButton size="small" onClick={() => setQualifyTarget(p.row)}><FactCheck fontSize="small" /></IconButton>
        </Stack>
      ),
    },
  ], [createOfferMutation])

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Comercial</Typography>
        <Typography variant="inherit" color="text.primary">Cereri client</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Cereri client</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormTarget('new')}>Cerere nouă</Button>
      </Box>
      <Box sx={{ height: 520, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>
      <ClientRequestFormDialog target={formTarget} onClose={() => setFormTarget(null)} />
      <QualifyRequestDialog target={qualifyTarget} onClose={() => setQualifyTarget(null)} />
      <AtpEstimateDialog target={atpTarget} onClose={() => setAtpTarget(null)} />
      <OfferFormDialog
        target={offerRequestId ? 'new' : null} sourceClientRequestId={offerRequestId} prefill={offerPrefill}
        onClose={() => { setOfferRequestId(null); setOfferPrefill(null) }}
      />
    </Stack>
  )
}
